import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useForm from '@components/form/hooks/useForm';

import { updateAdminUser } from '@actions/adminUsers';
import {
    getAdminUserIsPosting,
    getAdminUserPostSuccess,
    getAdminUsersPostError,
} from '@selectors/adminUsers';

import { AdminUser } from 'src/types/shared/AdminUser';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import { site as siteEnum } from 'lib/src/shared/enums/site';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import useSite from 'lib/src/hooks/useSite';
import usePrevious from 'lib/src/hooks/usePrevious';
export default function useUpdateAdmin({
    id,
    email,
    firstName,
    lastName,
    roles,
    site,
}: AdminUser): UpdateAdminReturn {
    const dispatch = useDispatch();

    const [formState, handleChange, setForm] = useForm({ email, firstName, lastName, roles, site });

    const handleSubmit = useCallback(() => {
        console.log({ formState });
        dispatch(updateAdminUser(id, formState));
    }, [dispatch, id, formState]);

    const isPosting = useSelector(getAdminUserIsPosting);
    const error = useSelector(getAdminUsersPostError);
    const postSuccess = useSelector(getAdminUserPostSuccess);

    const prevProps = usePrevious({ postSuccess, id });
    useEffect(() => {
        if (id && !prevProps.id) {
            setForm({ email, firstName, lastName, roles, site });
        }
    }, [email, firstName, id, lastName, prevProps.id, roles, setForm, site]);

    const userSite = useSite();
    const siteOptions: DropdownOption<siteEnum>[] = [
        { label: 'All', value: siteEnum.ALL },
        { label: 'IX', value: siteEnum.IX },
        { label: 'GA', value: siteEnum.GA },
    ];
    const shouldShowSiteOptions = userSite === siteEnum.ALL;

    return {
        handleSubmit,
        formState,
        handleChange,
        isPosting,
        error,
        postSuccess,
        siteOptions,
        shouldShowSiteOptions,
    };
}

interface FormState {
    email: string;
    firstName: string;
    lastName: string;
    roles: userRoles[];
    site: siteEnum;
}
interface UpdateAdminReturn {
    handleSubmit: () => void;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    isPosting: boolean;
    error: string | null;
    postSuccess: boolean;
    siteOptions: DropdownOption<siteEnum>[];
    shouldShowSiteOptions: boolean;
}
